import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'runup' | 'jump' | 'between' | 'done';

const START_X = 150;
const RUNUP_M = 16;
const PPM = SPRINT.pixelsPerMeter;
const START_BAR = 1.2; // metres
const BAR_STEP = 0.1;

/**
 * High Jump — a short run-up (mash), then tap STACCO near the bar. Clear it and the bar
 * rises; three misses ends your competition (best cleared height counts). Rising bar =
 * mounting tension. Best height vs the AI field.
 */
export class HighJump extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private velocity = 0;
  private runDist = 0;
  private acc = 0;
  private lastSide: Side | null = null;
  private cadence = 0;
  private lastTapAt = 0;
  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;
  private vClear = 1.6; // max clearable height contribution at full speed+timing

  private bar = START_BAR;
  private best = 0;
  private misses = 0;
  private cleared = false;
  private jumpT = 0;
  private jumpDur = 1;
  private apexM = 0; // this jump's peak height (m)
  private takeoffX = 0;

  private W = 0;
  private H = 0;
  private groundY = 0;
  private barX = 0;
  private uiS = 1;
  private vScale = 120; // px per metre (vertical)
  private camTarget = 0;
  private aiHeights: { name: string; species: string; nationality: string; metric: number }[] = [];

  private speedBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private jumpBtn!: Button;
  private tapZones!: TapZones;
  private barGfx!: Phaser.GameObjects.Graphics;

  constructor() {
    super('HighJump');
  }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.velocity = 0; this.runDist = 0; this.acc = 0; this.cadence = 0; this.lastSide = null;
    this.bar = START_BAR; this.best = 0; this.misses = 0; this.cleared = false; this.jumpT = 0; this.camTarget = 0;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.2);
    this.barX = START_X + RUNUP_M * PPM;
    this.vScale = (h * 0.42) / 1.4;
    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;
    this.vClear = 1.05 + ((this.athlete.stats.agility + this.athlete.stats.power) / 200) * 1.0;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.rig = createAthleteView(this, START_X, this.groundY, this.athlete, Phaser.Math.Clamp(h * 0.34, 170, 320));
    this.rig.container.setDepth(6);
    this.barGfx = this.add.graphics().setDepth(8);
    this.fetchWorldRecord('high_jump', 'distance', (m) => this.drawHeightRecord(m));

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiHeights = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, metric: Math.max(1.3, 1.55 + (o.jumpDistance - 6) * 0.12 + (Math.random() * 0.5 - 0.25)) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.takeoff());
    this.startCountdown();
  }

  private drawField(): void {
    const endX = this.barX + 6 * PPM;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(PALETTE.track, 1).fillRect(0, this.groundY, this.barX + 2 * PPM, this.H - this.groundY);
    // Landing mat behind the bar.
    g.fillStyle(0x3a6ea5, 1).fillRoundedRect(this.barX + 6, this.groundY - 30, 4 * PPM, 30 + (this.H - this.groundY), 8);
    g.fillStyle(0x2a3358, 1).fillRect(this.barX + 6, this.groundY, 4 * PPM, this.H - this.groundY);
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private barY(): number {
    return this.groundY - (this.bar - 0.6) * this.vScale;
  }

  /** Horizontal gold dashed line at the world-record height, near the bar. */
  private drawHeightRecord(m: number): void {
    const y = this.groundY - (m - 0.6) * this.vScale;
    if (y < 10) return;
    const g = this.add.graphics().setDepth(3);
    for (let xx = this.barX - 70; xx < this.barX + 80; xx += 14) g.fillStyle(PALETTE.sun, 0.9).fillRect(xx, y - 2, 8, 4);
    this.add.text(this.barX + 86, y, `🌍 ${m.toFixed(2)}m`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '13px', color: '#0b1020', fontStyle: 'bold', backgroundColor: '#ffd24a', padding: { x: 5, y: 2 },
    }).setOrigin(0, 0.5).setDepth(4);
  }

  private drawBar(knocked: boolean): void {
    const g = this.barGfx;
    g.clear();
    const postTop = this.groundY - (this.bar - 0.4) * this.vScale;
    // Two standards.
    g.fillStyle(0xdfe6ff, 1).fillRect(this.barX - 4, postTop, 6, this.groundY - postTop);
    g.fillStyle(0xdfe6ff, 1).fillRect(this.barX + 52, postTop, 6, this.groundY - postTop);
    // Crossbar (knocked = tilted red on the mat).
    const by = this.barY();
    if (knocked) {
      g.fillStyle(PALETTE.bad, 1).fillRect(this.barX - 2, by + 18, 64, 5);
    } else {
      g.fillStyle(PALETTE.laneAccent, 1).fillRect(this.barX - 2, by, 62, 5);
    }
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(26), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(280, this.W * 0.45);
    const topY = Math.max(50, this.H * 0.13);
    this.speedBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18 });
    this.add.text(this.W / 2, topY - 18, t('hud.speed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.bigText = this.add.text(this.W / 2, this.H * 0.42, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(78), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `⬆ ${t('btn.takeoff')} ${t('btn.space')}` : `⬆ ${t('btn.takeoff')}`;
    this.jumpBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.takeoff(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.good,
    });
    this.jumpBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) { AudioEngine.go(); this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 }); this.startAttempt(); }
        else AudioEngine.countdownTick();
      });
    });
  }

  private startAttempt(): void {
    this.velocity = 0; this.runDist = 0; this.cadence = 0; this.lastSide = null; this.cleared = false;
    this.phase = 'runup';
    this.drawBar(false);
    if (this.rig) { this.rig.container.setAngle(0); this.rig.container.x = START_X; this.rig.container.y = this.groundY; this.rig.setPose('idle'); }
    this.controls.setEnabled(true);
    this.lastTapAt = this.time.now / 1000;
    this.jumpBtn.setVisible(true);
    this.speedBar.setVisible(true);
    this.tapZones.setVisible(true);
    this.hud.setText(t('game.barMisses', { h: this.bar.toFixed(2), n: this.misses }));
  }

  private onTap(side: Side): void {
    if (this.phase !== 'runup') return;
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    this.cadence = Phaser.Math.Linear(this.cadence, 1 / dt, 0.45);
    this.lastTapAt = now;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) mult *= SPRINT.flowBonus;
    this.velocity = Math.min(this.maxSpeed * 1.06, this.velocity + SPRINT.tapImpulse * this.impulseScale * mult);
    this.tapZones.flash(side);
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  private takeoff(): void {
    if (this.phase !== 'runup' || !this.rig) return;
    const athleteX = START_X + this.runDist * PPM;
    // Timing: best when taking off just before the bar (within ~1.5m).
    const gap = (this.barX - athleteX) / PPM;
    const timingQ = Phaser.Math.Clamp(1 - Math.abs(gap - 1.0) / 2.0, 0.25, 1);
    const speedNorm = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.apexM = 0.7 + speedNorm * this.vClear * timingQ;
    this.cleared = this.apexM >= this.bar;
    this.takeoffX = athleteX;
    this.phase = 'jump';
    this.jumpT = 0;
    this.jumpDur = 1.1;
    this.controls.setEnabled(false);
    this.jumpBtn.setVisible(false);
    this.speedBar.setVisible(false);
    this.tapZones.setVisible(false);
    this.rig.setMotion(0);
    this.rig.setPose('celebrate');
    AudioEngine.go();
    this.vibrate(HAPTICS.goodTap);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    if (this.phase === 'intro' || this.phase === 'done' || this.phase === 'between') { this.rig?.update(dt); return; }
    if (this.phase === 'runup') { this.updateRunup(dt); return; }
    if (this.phase === 'jump') { this.updateJump(dt); }
  }

  private updateRunup(dt: number): void {
    this.acc += dt;
    let steps = 0;
    while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) {
      this.velocity *= SPRINT.dragPerStep;
      this.runDist += this.velocity * SIM_STEP;
      this.acc -= SIM_STEP;
      steps++;
    }
    const worldX = START_X + this.runDist * PPM;
    if (worldX > this.barX - 0.2 * PPM) { this.takeoff(); return; }
    this.rig.container.x = worldX;
    this.rig.container.y = this.groundY;
    const sn = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.rig.setMotion(sn);
    this.rig.update(dt);
    this.speedBar.set(sn);
    this.tapZones.update(dt);
    this.camTarget = Phaser.Math.Clamp(this.barX - this.W * 0.55, 0, this.cameras.main.getBounds().width - this.W);
    const toBar = (this.barX - worldX) / PPM;
    this.hud.setText(toBar > 0 ? t('game.barTakeoff', { h: this.bar.toFixed(2), m: toBar.toFixed(1) }) : t('game.takeoffNow'));
    if (this.time.now / 1000 - this.lastTapAt > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
  }

  private updateJump(dt: number): void {
    this.jumpT += dt;
    const p = Phaser.Math.Clamp(this.jumpT / this.jumpDur, 0, 1);
    const apexPx = (this.apexM - 0.6) * this.vScale;
    const x = this.takeoffX + (this.barX + 40 - this.takeoffX) * Math.min(1, p * 1.3);
    const y = this.groundY - Math.sin(p * Math.PI) * Math.max(20, apexPx);
    this.rig.container.x = this.cleared ? x : Math.min(x, this.barX - 6);
    this.rig.container.y = y;
    this.rig.container.setAngle(this.cleared ? -90 * Math.sin(p * Math.PI) * 0.3 : 0);
    this.rig.update(dt);
    if (!this.cleared && p > 0.45 && this.barGfx.alpha === 1) { this.drawBar(true); this.cameras.main.shake(120, 0.004); }
    if (p >= 1) this.resolveAttempt();
  }

  private resolveAttempt(): void {
    this.rig.container.setAngle(0);
    this.rig.container.y = this.groundY;
    this.phase = 'between';
    if (this.cleared) {
      this.best = this.bar;
      this.bigText.setText(`${this.bar.toFixed(2)}m ✔`).setColor('#4ade80').setAlpha(1).setScale(0.6);
      this.tweens.add({ targets: this.bigText, scale: 1, duration: 400, ease: 'Back.out' });
      AudioEngine.chime();
      AudioEngine.fanfare();
      this.vibrate(HAPTICS.finish);
      this.bar = Math.round((this.bar + BAR_STEP) * 100) / 100;
      this.time.delayedCall(1400, () => { this.tweens.add({ targets: this.bigText, alpha: 0, duration: 200 }); this.startAttempt(); });
    } else {
      this.misses++;
      this.bigText.setText(t('game.foul')).setColor('#ff5d6c').setAlpha(1).setScale(0.6);
      this.tweens.add({ targets: this.bigText, scale: 1, duration: 400, ease: 'Back.out' });
      this.vibrate(HAPTICS.finish);
      if (this.misses >= 3) this.time.delayedCall(1400, () => this.finish());
      else this.time.delayedCall(1400, () => { this.tweens.add({ targets: this.bigText, alpha: 0, duration: 200 }); this.startAttempt(); });
    }
  }

  private finish(): void {
    this.phase = 'done';
    this.controls.setEnabled(false);
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.best },
      ...this.aiHeights.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp((this.best - 0.8) * 650, 0, 1000));
    const reward = getDiscipline('high_jump').scoreToReward(score);
    this.bigText.setText(this.best > 0 ? t('game.mark', { h: this.best.toFixed(2) }) : t('game.noMark')).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    this.add.text(this.barX + 28, this.barY() - 14, `${countryFlag(this.athlete.nationality)} ${this.best.toFixed(2)}m`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(13 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold', backgroundColor: '#ffd24a', padding: { x: 6, y: 3 },
    }).setOrigin(0.5, 1).setDepth(9);
    const result: DisciplineResult = {
      disciplineId: 'high_jump', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.best, score, reward, rank, standings,
    };
    this.time.delayedCall(2400, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
